import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { setTenantSlug, bootstrapTenant, BLOCKED_STATES } from "@/lib/tenant";
import MasterShell from "@/components/master/MasterShell";
import MasterHome from "@/pages/master/MasterHome";
import TenantNew from "@/pages/master/TenantNew";
import TenantDetail from "@/pages/master/TenantDetail";
import Plans from "@/pages/master/Plans";
import Automation from "@/pages/master/Automation";
import Coupons from "@/pages/master/Coupons";
import PlatformBackup from "@/pages/master/PlatformBackup";
import Dre from "@/pages/Dre";
import TasksReport from "@/pages/TasksReport";
import Backup from "@/pages/Backup";
import ConferencePeriod from "@/pages/ConferencePeriod";
import Tutorial from "@/pages/Tutorial";
import TutorialSupport from "@/pages/master/TutorialSupport";
import Billing, { PaymentSuccess, BlockedScreen } from "@/pages/Billing";
import Tasks from "@/pages/Tasks";
import DemoEntry from "@/pages/DemoEntry";
import Signup, { SignupSuccess } from "@/pages/Signup";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { UnitProvider, useUnit } from "@/context/UnitContext";
import { LayoutProvider } from "@/context/LayoutContext";
import { Loading, EmptyState } from "@/components/kit";
import { api } from "@/lib/api";
import Shell from "@/components/Shell";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Conferences from "@/pages/Conferences";
import NewConference from "@/pages/NewConference";
import ConferenceEditor from "@/pages/ConferenceEditor";
import ConferenceHistory from "@/pages/ConferenceHistory";
import Products from "@/pages/Products";
import ProductForm from "@/pages/ProductForm";
import NamedList from "@/pages/NamedList";
import Menu from "@/pages/Menu";
import Suppliers from "@/pages/Suppliers";
import Units from "@/pages/Units";
import Users from "@/pages/Users";
import UserForm from "@/pages/UserForm";
import CmvTarget from "@/pages/CmvTarget";
import ReportEmails from "@/pages/ReportEmails";
import LayoutSettings from "@/pages/LayoutSettings";
import ShoppingLists from "@/pages/ShoppingLists";
import ShoppingListForm from "@/pages/ShoppingListForm";
import Recipes from "@/pages/Recipes";
import RecipeForm from "@/pages/RecipeForm";
import RecipeKitchen from "@/pages/RecipeKitchen";
import CmvAnalysis from "@/pages/CmvAnalysis";
import PdvCodes from "@/pages/PdvCodes";

function Protected({ children, mgmt, admin }) {
  const { user, loading, isMgmt, isAdmin, isSuper } = useAuth();
  const { ready } = useUnit();
  const { pathname } = useLocation();
  if (loading || (user && !isSuper && !ready)) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (isSuper) return <Navigate to="/master" replace />;
  if (BLOCKED_STATES.includes(user.tenant?.state) && !FREE_PATHS.includes(pathname)) return <BlockedScreen state={user.tenant.state} />;
  if ((mgmt && !isMgmt) || (admin && !isAdmin)) return <Navigate to="/" replace />;
  return children;
}

function SuperOnly({ children }) {
  const { user, loading, isSuper } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return isSuper ? children : <Navigate to="/" replace />;
}

function TenantEntry() {
  const { slug } = useParams();
  const { user, loading, logout } = useAuth();
  const qc = useQueryClient();
  const [done, setDone] = useState(null);
  useEffect(() => {
    if (loading) return;
    setTenantSlug(slug);
    const finish = async () => {
      const r = await api("/tenant").catch(() => ({ tenant: null }));
      if (!r.tenant) setTenantSlug(null);
      qc.resetQueries({ queryKey: ["layout"] });
      qc.resetQueries({ queryKey: ["tenant-public"] });
      setDone(r.tenant ? "ok" : "missing");
    };
    if (user && user.tenant?.slug !== slug) logout().then(finish); else finish();
  }, [slug, loading]); // eslint-disable-line react-hooks/exhaustive-deps
  if (done === "missing") return <div className="min-h-screen flex items-center justify-center p-6"><EmptyState title="Restaurante não encontrado" text="Confira o link de acesso enviado pelo seu fornecedor do sistema." testid="tenant-not-found" /></div>;
  return done ? <Navigate to="/login" replace /> : <Loading />;
}

const FREE_PATHS = ["/billing", "/payment/success"];

function PublicOnly({ children }) {
  const { user, loading, isSuper } = useAuth();
  if (loading) return <Loading />;
  return user ? <Navigate to={isSuper ? "/master" : "/"} replace /> : children;
}

const M = (el) => <Protected mgmt>{el}</Protected>;
const A = (el) => <Protected admin>{el}</Protected>;

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/c/:slug" element={<TenantEntry />} />
      <Route path="/cadastro" element={<Signup />} />
      <Route path="/demo" element={<DemoEntry />} />
      <Route path="/esqueci-senha" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
      <Route path="/redefinir-senha" element={<ResetPassword />} />
      <Route path="/cadastro/sucesso" element={<SignupSuccess />} />
      <Route path="/master" element={<SuperOnly><MasterShell /></SuperOnly>}>
        <Route index element={<MasterHome />} />
        <Route path="tenants/new" element={<TenantNew />} />
        <Route path="tenants/:id" element={<TenantDetail />} />
        <Route path="plans" element={<Plans />} />
        <Route path="automation" element={<Automation />} />
        <Route path="coupons" element={<Coupons />} />
        <Route path="backup" element={<PlatformBackup />} />
        <Route path="tutorial" element={<TutorialSupport />} />
      </Route>
      <Route element={<Protected><Shell /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="billing" element={A(<Billing />)} />
        <Route path="backup" element={A(<Backup />)} />
        <Route path="payment/success" element={<PaymentSuccess />} />
        <Route path="tasks" element={M(<Tasks />)} />
        <Route path="tasks/report" element={M(<TasksReport />)} />
        <Route path="conferences" element={<Conferences />} />
        <Route path="conferences/new" element={M(<NewConference />)} />
        <Route path="conference/:id" element={<ConferenceEditor />} />
        <Route path="conference-history/:id" element={<ConferenceHistory />} />
        <Route path="products" element={<Products />} />
        <Route path="products/new" element={M(<ProductForm key="new" />)} />
        <Route path="products/:id" element={M(<ProductForm key="edit" />)} />
        <Route path="categories" element={M(<NamedList kind="categories" />)} />
        <Route path="sectors" element={M(<NamedList kind="sectors" />)} />
        <Route path="menu-categories" element={M(<NamedList kind="menu-categories" />)} />
        <Route path="menu" element={<Menu />} />
        <Route path="tutorial" element={<Tutorial />} />
        <Route path="tutorial/:id" element={<Tutorial />} />
        <Route path="shopping-lists" element={<ShoppingLists />} />
        <Route path="shopping-lists/new" element={<ShoppingListForm />} />
        <Route path="shopping-lists/:id" element={<ShoppingListForm />} />
        <Route path="recipes" element={<Recipes />} />
        <Route path="recipes/new" element={M(<RecipeForm key="new" />)} />
        <Route path="recipes/:id" element={M(<RecipeForm key="edit" />)} />
        <Route path="recipes/:id/kitchen" element={<RecipeKitchen />} />
        <Route path="cmv-analysis" element={M(<CmvAnalysis />)} />
        <Route path="dre" element={M(<Dre />)} />
        <Route path="pdv-codes" element={M(<PdvCodes />)} />
        <Route path="suppliers" element={M(<Suppliers />)} />
        <Route path="units" element={M(<Units />)} />
        <Route path="users" element={A(<Users />)} />
        <Route path="users/new" element={A(<UserForm />)} />
        <Route path="users/:id" element={A(<UserForm />)} />
        <Route path="cmv-target" element={M(<CmvTarget />)} />
        <Route path="conference-period" element={M(<ConferencePeriod />)} />
        <Route path="report-emails" element={M(<ReportEmails />)} />
        <Route path="layout" element={A(<LayoutSettings />)} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

bootstrapTenant();

export default function App() {
  return (
    <BrowserRouter>
      <LayoutProvider>
        <AuthProvider>
          <UnitProvider>
            <AppRoutes />
            <Toaster position="top-center" richColors closeButton />
          </UnitProvider>
        </AuthProvider>
      </LayoutProvider>
    </BrowserRouter>
  );
}
